import { useEffect, useMemo, useState } from "react";
import { Users, Loader2, RefreshCw, Search, MessageCircle, Phone, Building2, DoorClosed, CalendarRange } from "lucide-react";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient from "@/lib/api";
import { formatDate } from "@/components/booking/bookingMeta";
import { TENANT_STATUS_LABELS, TENANT_STATUS_STYLES, RENTAL_PERIOD_LABELS } from "@/components/property/tenantMeta";

const STATUS_FILTERS = [
  { v: "all", l: "Semua" },
  { v: "active", l: "Aktif" },
  { v: "ending_soon", l: "Akan Berakhir" },
  { v: "ended", l: "Berakhir" },
];

// WhatsApp Click-to-Chat: normalize Indonesian numbers to 62xxxxxxxxxx (no API).
function waLink(phone) {
  if (!phone) return null;
  let d = String(phone).replace(/[^0-9]/g, "");
  if (!d) return null;
  if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (d.startsWith("62")) { /* already international */ }
  else if (d.startsWith("8")) d = "62" + d; // stored without leading 0
  return `https://wa.me/${d}`;
}

function StatusBadge({ status, testid }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${TENANT_STATUS_STYLES[status] || ""}`} data-testid={testid}>
      {TENANT_STATUS_LABELS[status] || status}
    </span>
  );
}

function WaButton({ phone, testid }) {
  const link = waLink(phone);
  if (!link) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors">
      <MessageCircle size={14} /> WhatsApp
    </a>
  );
}

export default function OwnerTenantsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [propId, setPropId] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    setLoading(true);
    apiClient.get("/owner/tenants").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const properties = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (r.property_id) map.set(r.property_id, r.property_name || "Properti"); });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const counts = useMemo(() => {
    const base = rows.filter((r) => propId === "all" || r.property_id === propId);
    return {
      total: base.length,
      active: base.filter((r) => r.status === "active").length,
      ending_soon: base.filter((r) => r.status === "ending_soon").length,
      ended: base.filter((r) => r.status === "ended").length,
    };
  }, [rows, propId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const termDigits = term.replace(/[^0-9]/g, "");
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (propId !== "all" && r.property_id !== propId) return false;
      // Rentang periode: tampilkan penghuni yang periodenya beririsan dengan [from, to].
      if (from && r.end_date && r.end_date < from) return false;
      if (to && r.start_date > to) return false;
      if (term) {
        const nameHit = (r.tenant_name || "").toLowerCase().includes(term);
        const phoneHit = termDigits && (r.phone || "").replace(/[^0-9]/g, "").includes(termDigits);
        if (!nameHit && !phoneHit) return false;
      }
      return true;
    });
  }, [rows, status, propId, q, from, to]);

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Users size={22} className="text-primary" /> Report Penghuni</h1>
          <p className="text-sm text-muted-foreground">Seluruh penghuni pada properti Anda.</p>
        </div>
        <button onClick={load} data-testid="owner-tenants-refresh"
          className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Status summary */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm" data-testid="owner-tenants-counts">
        <span className="px-3 py-1 rounded-full bg-secondary/60 border border-border font-semibold">Total {counts.total}</span>
        <span className="px-3 py-1 rounded-full border border-emerald-200 bg-emerald-100 text-emerald-700 font-semibold">Aktif {counts.active}</span>
        <span className="px-3 py-1 rounded-full border border-amber-200 bg-amber-100 text-amber-700 font-semibold">Akan Berakhir {counts.ending_soon}</span>
        <span className="px-3 py-1 rounded-full border border-border bg-secondary text-muted-foreground font-semibold">Berakhir {counts.ended}</span>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="flex flex-wrap gap-2" data-testid="owner-tenants-status-filters">
          {STATUS_FILTERS.map((f) => (
            <button key={f.v} onClick={() => setStatus(f.v)} data-testid={`owner-tenants-filter-${f.v}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${status === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>
              {f.l}
            </button>
          ))}
        </div>
        <div className="flex flex-1 gap-3">
          <select value={propId} onChange={(e) => setPropId(e.target.value)} data-testid="owner-tenants-property-filter"
            className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary min-w-0">
            <option value="all">Semua Properti</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="relative flex-1 min-w-0">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="owner-tenants-search"
              placeholder="Cari nama atau nomor telepon"
              className="w-full rounded-xl border border-border bg-secondary/40 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
        </div>
      </div>

      {/* Filter rentang periode sewa */}
      <div className="flex flex-wrap items-center gap-2 mb-5 text-sm" data-testid="owner-tenants-period-filter">
        <span className="text-muted-foreground inline-flex items-center gap-1.5"><CalendarRange size={15} /> Periode:</span>
        <label className="text-muted-foreground">Dari</label>
        <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="owner-tenants-period-from"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-1.5 outline-none focus:ring-2 focus:ring-primary" />
        <label className="text-muted-foreground">sampai</label>
        <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="owner-tenants-period-to"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-1.5 outline-none focus:ring-2 focus:ring-primary" />
        {(from || to) && (
          <button onClick={() => { setFrom(""); setTo(""); }} data-testid="owner-tenants-period-clear"
            className="px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 transition-colors">
            Reset
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="owner-tenants-empty">
          <Users size={30} className="mx-auto mb-2" /> Tidak ada penghuni yang cocok dengan filter.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto rounded-2xl border border-border" data-testid="owner-tenants-table">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-secondary/50 text-left">
                <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:text-muted-foreground">
                  <th>Nama</th><th>Telepon</th><th>Properti</th><th>Kamar</th><th>Periode</th><th>Mulai</th><th>Berakhir</th><th>Status</th><th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => (
                  <tr key={t.id} data-testid={`tenant-row-${i}`} className="border-t border-border [&>td]:px-4 [&>td]:py-3">
                    <td className="font-semibold" data-testid={`tenant-row-name-${i}`}>{t.tenant_name}</td>
                    <td data-testid={`tenant-row-phone-${i}`}>{t.phone || "—"}</td>
                    <td>{t.property_name || "—"}</td>
                    <td>{t.room_number || "—"}</td>
                    <td>{RENTAL_PERIOD_LABELS[t.rental_period] || t.rental_period}</td>
                    <td>{formatDate(t.start_date)}</td>
                    <td>{t.end_date ? formatDate(t.end_date) : "Tanpa batas"}</td>
                    <td><StatusBadge status={t.status} testid={`tenant-row-status-${i}`} /></td>
                    <td><WaButton phone={t.phone} testid={`tenant-row-wa-${i}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile / tablet cards */}
          <div className="lg:hidden space-y-3" data-testid="owner-tenants-cards">
            {filtered.map((t, i) => (
              <div key={t.id} data-testid={`tenant-card-${i}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold">{t.tenant_name}</span>
                  <StatusBadge status={t.status} testid={`tenant-card-status-${i}`} />
                </div>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p className="inline-flex items-center gap-1.5"><Building2 size={13} /> {t.property_name || "—"} · <DoorClosed size={13} /> {t.room_number || "—"}</p>
                  {t.phone && <p className="inline-flex items-center gap-1.5"><Phone size={13} /> {t.phone}</p>}
                  <p>{formatDate(t.start_date)} — {t.end_date ? formatDate(t.end_date) : "Tanpa batas"} <span className="text-xs">({RENTAL_PERIOD_LABELS[t.rental_period] || t.rental_period})</span></p>
                </div>
                <div className="mt-3"><WaButton phone={t.phone} testid={`tenant-card-wa-${i}`} /></div>
              </div>
            ))}
          </div>
        </>
      )}
    </OwnerLayout>
  );
}
